import { callable } from './callable.js'
import { uploadWorkspaceFiles } from './storageHubService.js'

export interface EduAttachmentRef {
  id?: string
  fileId?: string
  name: string
  size?: number
  contentType?: string
  downloadUrl?: string
}

export interface EduFeedPost {
  id: string
  workspaceId: string
  kind: 'TEACHING' | 'TRAINING'
  nodeIds: string[]
  allClasses: boolean
  reach: 'TAGGED' | 'CLASS'
  body: string
  attachments: EduAttachmentRef[]
  hasMedia: boolean
  taggedLearnerIds: string[]
  visible: boolean
  editedAt: unknown | null
  deletedAt: unknown | null
  createdBy: string
  createdByName: string
  createdAt: unknown
  reactionCounts: Record<string, number>
  seenCount: number
  requestId: string
  consentChanged?: string[]
}

export interface PostEduFeedPostInput {
  workspaceId: string
  kind: 'TEACHING' | 'TRAINING'
  target: {
    allClasses?: boolean
    nodeIds?: string[]
  }
  reach: 'TAGGED' | 'CLASS'
  body: string
  fileIds?: string[]
  taggedLearnerIds?: string[]
  requestId: string
}

export interface EditEduFeedPostInput {
  workspaceId: string
  postId: string
  body?: string
  fileIds?: string[]
  taggedLearnerIds?: string[]
  reach?: 'TAGGED' | 'CLASS'
}

export interface DeleteEduFeedPostInput {
  workspaceId: string
  postId: string
}

export interface ListEduFeedPostsInput {
  workspaceId: string
  nodeId?: string
  allClasses?: boolean
  cursor?: string
  pageSize?: number
}

export interface ListEduFeedPostsResult {
  posts: EduFeedPost[]
  nextCursor: string | null
}

export interface PostEduFeedPostResult {
  success: boolean
  post: EduFeedPost
  created: boolean
}

export interface EditEduFeedPostResult {
  success: boolean
  post: EduFeedPost
}

export async function postEduFeedPost(
  input: PostEduFeedPostInput,
): Promise<PostEduFeedPostResult> {
  return callable('postEduFeedPost', input)
}

export async function editEduFeedPost(
  input: EditEduFeedPostInput,
): Promise<EditEduFeedPostResult> {
  return callable('editEduFeedPost', input)
}

export async function deleteEduFeedPost(
  input: DeleteEduFeedPostInput,
): Promise<{ success: boolean }> {
  return callable('deleteEduFeedPost', input)
}

export async function listEduFeedPosts(
  input: ListEduFeedPostsInput,
): Promise<ListEduFeedPostsResult> {
  return callable('listEduFeedPosts', input)
}

export interface ListFamilyFeedInput {
  workspaceId: string
  learnerId: string
  cursor?: string
  pageSize?: number
}

export interface FamilyFeedItem extends Omit<EduFeedPost, 'seenCount'> {
  myReactions: string[]
  seenByMe: boolean
  seenCount?: never
}

export interface ListFamilyFeedResult {
  posts: FamilyFeedItem[]
  nextCursor: string | null
}

export interface ReactEduFeedPostInput {
  workspaceId: string
  postId: string
  learnerId: string
  emoji: string
}

export interface ReactEduFeedPostResult {
  success: boolean
  reacted: boolean
  reactionCounts: Record<string, number>
}

export interface MarkEduFeedPostSeenInput {
  workspaceId: string
  postId: string
  learnerId: string
}

export interface MarkEduFeedPostSeenResult {
  success: boolean
}

export async function listFamilyFeed(
  input: ListFamilyFeedInput,
): Promise<ListFamilyFeedResult> {
  return callable('listFamilyFeed', input)
}

export async function reactEduFeedPost(
  input: ReactEduFeedPostInput,
): Promise<ReactEduFeedPostResult> {
  return callable('reactEduFeedPost', input)
}

export async function markEduFeedPostSeen(
  input: MarkEduFeedPostSeenInput,
): Promise<MarkEduFeedPostSeenResult> {
  return callable('markEduFeedPostSeen', input)
}

export interface UploadFeedPostFilesInput {
  workspaceId: string
  requestId: string
  files: File[]
  onProgress?: (completed: number, total: number, fileName: string) => void
}

export async function uploadFeedPostFiles(
  input: UploadFeedPostFilesInput,
): Promise<string[]> {
  if (input.files.length === 0) return []
  for (const f of input.files) {
    if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) {
      throw new Error('Only image and video files are supported for feed posts.')
    }
  }
  const results = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: input.files,
    visibility: 'PRIVATE',
    artifactType: 'EDU_FEED_POST',
    artifactId: input.requestId,
    onProgress: input.onProgress,
  })
  const fileIds: string[] = []
  for (const res of results) {
    if (!res.fileId) {
      throw new Error('Upload completed without returning a file ID.')
    }
    fileIds.push(res.fileId)
  }
  return fileIds
}
